/**
 * FolderSelect
 * Dropdown for choosing a folder (or root) with hierarchical labels.
 */

import { useEffect, useMemo, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, type ApiListResponse } from '@/lib/api';

interface FolderItem {
    id: string;
    name: string;
    parent: string | null;
}

interface FolderOption {
    id: string;
    label: string;
}

interface FolderSelectProps {
    value: string | null | undefined;
    onChange: (value: string | null) => void;
    placeholder?: string;
    includeRootOption?: boolean;
}

function buildFolderOptions(folders: FolderItem[]): FolderOption[] {
    const byParent = (parentId: string | null) =>
        folders.filter(folder => folder.parent === parentId).sort((a, b) => a.name.localeCompare(b.name));

    const options: FolderOption[] = [];

    const walk = (parentId: string | null, trail: string[]) => {
        for (const folder of byParent(parentId)) {
            const labelParts = [...trail, folder.name];
            options.push({ id: folder.id, label: labelParts.join(' / ') });
            walk(folder.id, labelParts);
        }
    };

    walk(null, []);
    return options;
}

export function FolderSelect({ value, onChange, placeholder = 'Choose a folder', includeRootOption = true }: FolderSelectProps) {
    const [folders, setFolders] = useState<FolderItem[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let mounted = true;
        const fetchFolders = async () => {
            setLoading(true);
            try {
                const response = await api.get<ApiListResponse<FolderItem>>('/folders?limit=-1&sort=name');
                if (mounted) {
                    setFolders(response.data || []);
                }
            } catch (error) {
                console.error('Error fetching folders', error);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        fetchFolders();
        return () => {
            mounted = false;
        };
    }, []);

    const folderOptions = useMemo(() => buildFolderOptions(folders), [folders]);
    const selectValue = value ?? '_root';

    return (
        <div className="space-y-1">
            <Select
                value={selectValue}
                onValueChange={(val) => onChange(val === '_root' ? null : val)}
                disabled={loading}
            >
                <SelectTrigger>
                    <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent>
                    {includeRootOption && (
                        <SelectItem value="_root">Root (no folder)</SelectItem>
                    )}
                    {folderOptions.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                            {option.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            {loading && (
                <p className="text-xs text-muted-foreground">Loading folders...</p>
            )}
        </div>
    );
}
